import { useI18n } from '@/context/I18nContext';
import { formatTime } from '@/lib/format';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { TyreTrackExpectation } from '@/types/telemetry';

interface TyreExpectationsTableProps {
  expectations: TyreTrackExpectation[];
  loading: boolean;
}

export function TyreExpectationsTable({ expectations, loading }: TyreExpectationsTableProps) {
  const { t } = useI18n();

  if (loading) {
    return <LoadingSpinner />;
  }

  // Only show tracks with data, sorted by expected median ascending
  const withData = expectations
    .filter(e => e.matchingLapCount > 0)
    .sort((a, b) => a.expectedMedianLapMs - b.expectedMedianLapMs);

  if (withData.length === 0) {
    return (
      <div className="card flex flex-col items-center justify-center py-12 gap-2 text-center">
        <span className="text-2xl opacity-60">🌍</span>
        <p className="text-sm text-text-secondary">{t('tyre.noExpectations')}</p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      {/* Desktop table */}
      <table className="w-full text-sm border-collapse hidden md:table">
        <thead>
          <tr className="divider">
            <th className="text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted px-4 py-2.5">{t('tyre.trackColumn')}</th>
            <th className="text-right text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted px-4 py-2.5">{t('tyre.expected')}</th>
          </tr>
        </thead>
        <tbody>
          {withData.map((exp, i) => (
            <tr
              key={`${exp.trackName}-${i}`}
              className={`hover:bg-bg-hover transition-colors ${i < withData.length - 1 ? 'divider' : ''}`}
            >
              <td className="px-4 py-3 text-text-primary font-medium">{exp.trackName}</td>
              <td className="px-4 py-3 text-right mono font-semibold text-green">{formatTime(exp.expectedMedianLapMs)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile cards */}
      <div className="md:hidden">
        {withData.map((exp, i) => (
          <div
            key={`${exp.trackName}-${i}`}
            className={`p-4 flex items-center justify-between hover:bg-bg-hover transition-colors ${i < withData.length - 1 ? 'divider' : ''}`}
          >
            <span className="text-sm font-medium text-text-primary">{exp.trackName}</span>
            <span className="mono text-sm text-green font-semibold">{formatTime(exp.expectedMedianLapMs)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
